import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Chip,
  InputLabel,
  OutlinedInput,
  FormControl,
  Button,
  FormHelperText,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../context/UserContext";

const sdgOptions = [
  "1. No Poverty",
  "2. Zero Hunger",
  "3. Good Health and Well-being",
  "4. Quality Education",
  "5. Gender Equality",
  "6. Clean Water and Sanitation",
  "7. Affordable and Clean Energy",
  "8. Decent Work and Economic Growth",
  "9. Industry, Innovation and Infrastructure",
  "10. Reduced Inequality",
  "11. Sustainable Cities and Communities",
  "12. Responsible Consumption and Production",
  "13. Climate Action",
  "14. Life Below Water",
  "15. Life on Land",
  "16. Peace, Justice and Strong Institutions",
  "17. Partnerships for the Goals",
];

const sectorOptions = ["Health", "Education", "Livelihood", "Environment"];
const stateOptions = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const AddProjects = ({ open, handleClose, onProjectAdded, editProject }) => {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    name: "",
    sdgs: [],
    sectors: [],
    budget: "",
    states: [],
  });
  const [orgName, setOrgName] = useState("");
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Prefill if editing
  useEffect(() => {
    if (editProject) {
      setFormData({
        name: editProject.name || "",
        sdgs: editProject.sdgs || [],
        sectors: editProject.sectors || [],
        budget: editProject.budget || "",
        states: editProject.states || [],
      });
    } else {
      setFormData({ name: "", sdgs: [], sectors: [], budget: "", states: [] });
    }
  }, [editProject, open]);

  // Fetch organization name
  useEffect(() => {
    const fetchOrgName = async () => {
      if (user?.uid) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userDocRef);
          if (userSnap.exists()) {
            const data = userSnap.data();
            setOrgName(data.organizationName || "");
          }
        } catch (error) {
          console.error("Error fetching user doc:", error);
        }
      }
    };
    fetchOrgName();
  }, [user?.uid]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: typeof value === "string" ? value.split(",") : value,
    }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.sdgs.length) newErrors.sdgs = true;
    if (!formData.sectors.length) newErrors.sectors = true;
    if (!formData.budget || Number(formData.budget) <= 0)
      newErrors.budget = true;
    if (!formData.states.length) newErrors.states = true;

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    try {
      if (!user?.organizationId || !orgName) {
        showSnackbar("User organization info missing", "error");
        return;
      }

      if (editProject) {
        // Update
        await updateDoc(doc(db, "projects", editProject.id), { ...formData });
        showSnackbar("Project updated successfully!", "success");
      } else {
        // Add
        await addDoc(collection(db, "projects"), {
          ...formData,
          createdAt: serverTimestamp(),
          organizationId: user.organizationId,
          organizationName: orgName,
        });
        showSnackbar("Project added successfully!", "success");
      }

      onProjectAdded();
      handleClose();
      setFormData({ name: "", sdgs: [], sectors: [], budget: "", states: [] });
      setErrors({});
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to save project", "error");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {editProject ? "Edit Project" : "Add New Project"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Organization"
            fullWidth
            margin="normal"
            value={orgName || ""}
            disabled
          />

          <TextField
            label={
              <span>
                Project Name <span style={{ color: "red" }}>*</span>
              </span>
            }
            fullWidth
            margin="normal"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            helperText={errors.name && "Project name is required"}
          />

          <FormControl fullWidth margin="normal" error={errors.sdgs}>
            <InputLabel required>SDGs</InputLabel>
            <Select
              multiple
              value={formData.sdgs}
              onChange={handleChange("sdgs")}
              input={<OutlinedInput label="SDGs" />}
              renderValue={(selected) =>
                selected.map((val) => (
                  <Chip key={val} label={val} sx={{ mr: 0.5 }} />
                ))
              }
            >
              {sdgOptions.map((sdg) => (
                <MenuItem key={sdg} value={sdg}>
                  {sdg}
                </MenuItem>
              ))}
            </Select>
            {errors.sdgs && (
              <FormHelperText>Select at least one SDG</FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth margin="normal" error={errors.sectors}>
            <InputLabel required>Sectors</InputLabel>
            <Select
              multiple
              value={formData.sectors}
              onChange={handleChange("sectors")}
              input={<OutlinedInput label="Sectors" />}
              renderValue={(selected) =>
                selected.map((val) => (
                  <Chip key={val} label={val} sx={{ mr: 0.5 }} />
                ))
              }
            >
              {sectorOptions.map((sector) => (
                <MenuItem key={sector} value={sector}>
                  {sector}
                </MenuItem>
              ))}
            </Select>
            {errors.sectors && (
              <FormHelperText>Select at least one sector</FormHelperText>
            )}
          </FormControl>

          <TextField
            label={
              <span>
                Budget <span style={{ color: "red" }}>*</span>
              </span>
            }
            type="number"
            fullWidth
            margin="normal"
            value={formData.budget}
            onChange={(e) =>
              setFormData({ ...formData, budget: e.target.value })
            }
            error={errors.budget}
            helperText={errors.budget && "Enter a valid budget"}
          />

          <FormControl fullWidth margin="normal" error={errors.states}>
            <InputLabel required>States</InputLabel>
            <Select
              multiple
              value={formData.states}
              onChange={handleChange("states")}
              input={<OutlinedInput label="States" />}
              renderValue={(selected) =>
                selected.map((val) => (
                  <Chip key={val} label={val} sx={{ mr: 0.5 }} />
                ))
              }
            >
              {stateOptions.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
            {errors.states && (
              <FormHelperText>Select at least one state</FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ color: "#645792" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#645792",
              "&:hover": { backgroundColor: "#4e4574" },
            }}
          >
            {editProject ? "Update" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default AddProjects;
